/**
 * Image Error Handler Plugin for Native Interactive Site Map (ISM)
 *
 * Handles broken image errors in card and detail-panel components by replacing them with a
 * placeholder image. Listens for image load failures and replaces broken images with the
 * placeholder from IRSDomain. Binds one handler per container that can hold
 * `[data-siteplan-card-image]` images — today that's [data-panel] (always present) and
 * [data-secondary-panel] (desktop only — see components/secondaryPanelController.dom.js — so on
 * mobile this plugin simply finds one container instead of two, nothing else to special-case).
 * Registration order in index.js puts floorPlanDetail.plugin.js before this plugin, so on
 * desktop the secondary panel already exists in the DOM by the time this queries for it.
 *
 * Lifecycle:
 * - On start: Waits for runtime configuration to be available, then binds error handlers to
 *   every container found
 * - On stop: Unbinds all error handlers and cleans up subscriptions
 *
 * @module nativeInteractiveSiteMap/plugins/imageError.plugin
 */

import { take } from 'rxjs/operators';
import createImageErrorHandler from '../handlers/imageErrorHandler';

export default function createImageErrorPlugin() {
  const state = {
    imageErrorHandlers: [],
    subscriptions: [],
  };

  return {
    name: 'image-error-handler',

    async init(context) {
      context.state.imageErrorHandler = {
        started: false,
        ready: false,
      };
    },

    async start(context) {
      if (context.state.imageErrorHandler?.started) {
        return;
      }

      // Find every container image errors can occur in.
      const panel = context.root.querySelector('[data-panel]');
      const secondaryPanel = context.root.querySelector('[data-secondary-panel]');
      const containers = [panel, secondaryPanel].filter(Boolean);

      if (containers.length === 0) {
        console.warn('Image error handler: Could not find panel container in ISM root');
        return;
      }

      context.state.imageErrorHandler = { started: true, ready: false };

      // Subscribe to configuration$ (emits once when runtime config is available)
      const configurationSubscription = context.services.configuration$
        .pipe(take(1))
        .subscribe(({ imageResizeService }) => {
          const placeholderImageUrl = `${imageResizeService}/images/placeholder.png`;
          state.imageErrorHandlers = containers.map(
            (container) => createImageErrorHandler(container, placeholderImageUrl)
          );
          context.state.imageErrorHandler = { started: true, ready: true };
        });

      state.subscriptions.push(configurationSubscription);
    },

    async stop(context) {
      state.imageErrorHandlers.forEach((handler) => handler.unbind());
      state.imageErrorHandlers = [];

      state.subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
      state.subscriptions = [];

      context.state.imageErrorHandler = { started: false, ready: false };
    },
  };
}
